/**
 * fieldErrorCopy: a field error as one plain sentence. The sentence names
 * the field by the label its box shows and says what to do, from the
 * field's allowed values or rule. The validator's own words never reach
 * it.
 *
 * The sentence is chosen per failure, not per field: `fieldSentence`
 * looks at the value the screen sent to decide which rule it breaks, so a
 * malformed date reads as a malformed date and a future date as a future
 * date. When the value breaks none of the checks the browser can make (a
 * cross-field rule, a stale vocabulary), the sentence is
 * "Check {label} and try again.", which is true for any failure.
 *
 * Where the rules and labels come from:
 *   - record fields: `ruleFromSpec` over the field's registry spec, with
 *     the label the field's box shows (`fieldLabel` unless the screen
 *     names its own);
 *   - the time card: TIME_ENTRY_FIELDS;
 *   - the metrics upload: METRICS_LABELS through `metricsFieldLabel`.
 */

import { humanizeToken } from '@/lib/fieldLabels';
import { practiceTodayIso } from '@/lib/practiceDate';
import type { PayloadFieldSpec } from '@/lib/noteVocabulary';
import { TIME_ENTRY_KIND_LABEL } from '@/lib/timeEntryKinds';
import { contactSentence, FALLBACK_SUPPORT_CONTACT, type SupportContact } from '@/lib/errorCopy';

export type FieldRule =
  | { kind: 'one_of'; values: readonly string[] } // display labels, in order
  | { kind: 'some_of'; values: readonly string[] }
  | { kind: 'from_list' }
  | { kind: 'date' }
  | { kind: 'time' }
  /** `today` is the day the date may not pass (YYYY-MM-DD); the device's
   *  day when absent. */
  | { kind: 'not_future'; today?: string }
  | { kind: 'text' }
  | { kind: 'one_line'; max: number }
  | { kind: 'yes_no' }
  | { kind: 'hours' }
  | { kind: 'sunday' }
  | { kind: 'format' }
  /** A text the person typed, over the longest the field takes. */
  | { kind: 'max_length'; max: number }
  /** A value Olive sent and the person never typed: the app's own fault,
   *  reported to the office. */
  | { kind: 'app_fault' };

export const FIELD_ERROR_FALLBACK =
  'Nothing was saved yet: a box that needs an answer is blank, or a date or number is out of range. Fix that box, then save again.';
export const METRICS_ERROR_FALLBACK =
  "That paste couldn't be read. Copy the whole block from the Monday routine again and paste it here.";

const DATE_EXAMPLE = '2026-07-12';

/** "A", "A or B", "A, B or C". */
export function orList(values: readonly string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  return `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`;
}

/** Undefined, null, '' or whitespace. */
function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** YYYY-MM-DD naming a calendar day. */
function isRealDate(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (m === null) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const day = new Date(Date.UTC(y, mo - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === mo - 1 && day.getUTCDate() === d;
}

/** The sent value as a list of entries, for a some_of rule. */
function entriesOf(v: unknown): unknown[] {
  if (isEmpty(v)) return [];
  return Array.isArray(v) ? v : [v];
}

/** A sentence that opens on the box's label reads as one. */
function leading(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function fallback(label: string, contact: SupportContact): string {
  return contactSentence(
    `${leading(label)} isn't in a shape that can be saved. Check it and save again, and if it still won't save, tell {contact_system}.`,
    contact,
  );
}

function hoursSentence(label: string, sent: unknown, contact: SupportContact): string {
  const text = typeof sent === 'number' ? String(sent) : typeof sent === 'string' ? sent.trim() : '';
  const n = text === '' ? Number.NaN : Number(text);
  if (!Number.isFinite(n)) return `Enter ${label} as a number of hours, with a decimal point for part hours: 1.5.`;
  if (n <= 0) return `Enter ${label} as a number above 0.`;
  const decimals = /\.(\d+)$/.exec(text)?.[1]?.length ?? 0;
  if (decimals > 2) return `Enter ${label} with no more than two decimal places.`;
  if (n >= 10000) return `Enter ${label} as a number under 10,000.`;
  return fallback(label, contact);
}

/** One plain sentence for one field's failure. `sent` is the value the
 *  screen sent for it (undefined when the screen sent none). */
export function fieldSentence(
  label: string,
  rule: FieldRule,
  sent?: unknown,
  contact: SupportContact = FALLBACK_SUPPORT_CONTACT,
): string {
  switch (rule.kind) {
    case 'one_of':
      return `Choose ${orList(rule.values)} for ${label}.`;
    case 'some_of': {
      const entries = entriesOf(sent);
      const listed = (e: unknown): boolean =>
        typeof e === 'string' && (rule.values.includes(e) || rule.values.includes(humanizeToken(e)));
      if (entries.length === 0 || !entries.every(listed)) {
        return `Choose one or more of ${orList(rule.values)} for ${label}.`;
      }
      return `That mix isn't allowed for ${label}. Remove one choice, then save again.`;
    }
    case 'from_list':
      return `Choose ${label} from the list.`;
    case 'time':
      if (isEmpty(sent)) return `Enter a time for ${label}.`;
      return `Enter ${label} as a time with AM or PM: 2:30 PM.`;
    case 'date':
      if (isEmpty(sent)) return `Enter a date for ${label}.`;
      if (!isRealDate(sent)) return `Enter ${label} as a full date: year, month, day, ${DATE_EXAMPLE}.`;
      return fallback(label, contact);
    case 'not_future':
      if (!isRealDate(sent)) return `Enter ${label} as a full date: year, month, day, ${DATE_EXAMPLE}.`;
      if (sent.trim() > (rule.today ?? practiceTodayIso())) {
        return `${label} can't be in the future. Enter today or an earlier day.`;
      }
      return fallback(label, contact);
    case 'text':
      return isEmpty(sent) ? `Fill in ${label}.` : fallback(label, contact);
    case 'one_line':
      if (typeof sent === 'string' && /[\r\n]/.test(sent)) return `Keep ${label} to one line.`;
      if (typeof sent === 'string' && sent.length > rule.max) {
        return `Keep ${label} to ${rule.max} characters or fewer.`;
      }
      return fallback(label, contact);
    case 'hours':
      return hoursSentence(label, sent, contact);
    case 'yes_no':
      return `Choose yes or no for ${label}.`;
    case 'sunday':
      return `Enter a Sunday for ${label}, as year, month, day: ${DATE_EXAMPLE}.`;
    case 'format':
      return `${leading(label)} isn't in the Monday routine's format. Copy the block from the routine again and paste it.`;
    case 'max_length':
      return `Keep ${label} to ${rule.max.toLocaleString('en-US')} characters or fewer.`;
    case 'app_fault':
      return contactSentence(
        `${leading(label)} couldn't be saved, and nothing you typed caused it, so nothing changed. Try again later, and if it still won't save, tell {contact_system}.`,
        contact,
      );
    default:
      return fallback(label, contact);
  }
}

/** The rule for a registry field, by its kind; null for a link id, which
 *  reads the fallback. */
export function ruleFromSpec(spec: PayloadFieldSpec): FieldRule | null {
  switch (spec.kind) {
    case 'enum':
      return { kind: 'one_of', values: (spec.values ?? []).map(humanizeToken) };
    case 'enum_set':
      return { kind: 'some_of', values: (spec.values ?? []).map(humanizeToken) };
    case 'risk_category':
      return { kind: 'from_list' };
    case 'date':
      return { kind: 'date' };
    case 'time':
      return { kind: 'time' };
    case 'string':
    case 'email':
      return { kind: 'text' };
    case 'boolean':
      return { kind: 'yes_no' };
    case 'uuid':
      return null;
    default:
      return null;
  }
}

/** The time card's fields, labelled as the card labels them. */
export const TIME_ENTRY_FIELDS: Readonly<Record<string, { label: string; rule: FieldRule }>> = {
  kind: { label: 'Kind', rule: { kind: 'one_of', values: Object.values(TIME_ENTRY_KIND_LABEL) } },
  hours: { label: 'Hours', rule: { kind: 'hours' } },
  occurred_on: { label: 'Date', rule: { kind: 'not_future' } },
  note: { label: 'Note', rule: { kind: 'one_line', max: 200 } },
};

/** One label per key the metrics validator names. */
export const METRICS_LABELS: Readonly<Record<string, string>> = {
  week_ending: 'Week ending',
  status: 'Status',
  source_note: 'Source note',
  sections: 'Sections',
  weeks: 'Weeks',
  total_revenue: 'Total revenue',
  cm_revenue: 'Care management revenue',
  spice_revenue: 'SPICE revenue',
  total_hours: 'Total hours',
  ar_total: 'Total receivables',
  ar_collectible_60plus: 'Collectible receivables over 60 days',
  ar_reserved: 'Reserved receivables',
  cash: 'Cash',
  plan_total: 'Planned total revenue',
  plan_cm: 'Planned care management revenue',
  plan_spice: 'Planned SPICE revenue',
  clients_touched: 'Clients touched',
  crm_new_leads: 'New leads',
  crm_active_pipeline: 'Active pipeline',
  crm_partner_touches: 'Partner touches',
  monthly: 'Monthly revenue',
  ar_aging: 'Receivables aging',
  collections: 'Collections',
  expenses: 'Expenses',
  clinician_hours: 'Clinician hours',
  top_clients: 'Top clients',
  quiet_clients: 'Quiet clients',
  partners: 'Partners',
  narrative: 'Narrative',
  hours_note: 'Hours note',
  cash_ar_note: 'Cash and receivables note',
  sources_note: 'Sources note',
  month: 'Month',
  actual: 'Actual',
  plan: 'Plan',
  prior_year: 'Prior year',
  buckets: 'Buckets',
  bucket: 'Bucket',
  collectible: 'Collectible',
  reserved: 'Reserved',
  note: 'Note',
  rows: 'Rows',
  client_name: 'Client name',
  aged_amount: 'Aged amount',
  total_on_book: 'Total on book',
  spend_this_week: 'Spend this week',
  client_billable: 'Client billable',
  to_invoice_with_markup: 'To invoice with markup',
  month_to_date: 'Month to date',
  name: 'Name',
  role: 'Role',
  hours: 'Hours',
  services: 'Services',
  avg_weekly_revenue: 'Average weekly revenue',
  last_seen: 'Last seen',
  avg_week_dollars: 'Average week',
  last_billed: 'Last billed',
  tier: 'Tier',
  touches: 'Touches',
  pie: 'PIE date',
};

/**
 * A metrics path in words: one segment gives its label; more give
 * "{last} in {first}" ("collections.rows[].client_name" reads "Client
 * name in Collections"). Null when any segment has no label.
 */
export function metricsFieldLabel(path: string): string | null {
  const segments = path.split('.').map((s) => s.replace(/\[\]/g, ''));
  if (segments.some((s) => !Object.prototype.hasOwnProperty.call(METRICS_LABELS, s))) return null;
  const first = METRICS_LABELS[segments[0]!]!;
  if (segments.length === 1) return first;
  return `${METRICS_LABELS[segments[segments.length - 1]!]!} in ${first}`;
}
